import React, { Fragment, FC, useState } from 'react';
import { Alert, Box, Button, Divider, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { MaterialListType, MaterialPriceList, PriceListMaterial } from 'models/material';
import { ApolloCache, Reference, useMutation, useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import {
    getMaterialPriceListsTemplate,
    deleteMaterialPriceListTemplate,
    addDefaultMaterialPriceListTemplate,
    editMaterialPriceListRowTemplate,
    getLabMaterialsTemplate,
    addMaterialToPriceListTemplate,
    deleteMaterialPriceListRowTemplate,
} from 'apollo/templates/material';
import { useTranslation } from 'react-i18next';
import MaterialPriceListsActions from './material-price-lists-actions.component';
import MaterialPriceListsFilters from './material-price-lists-filters.component';
import MaterialPriceListsItem from './material-price-lists-item.component';
import EditMaterialPriceListDialog from './edit-material-price-list-dialog/edit-material-price-list-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import AddMaterialPriceListDialog from './add-material-price-list-dialog/add-material-price-list-dialog.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { OrderBy } from 'models/search-result';
import { DEFAULT_MATERIAL_PRICE_LIST_ORDER, sortPriceListMaterials, toggleOrderBy } from 'utils/price-list-sort';

const MaterialPriceLists: FC = () => {
    const { t: translate } = useTranslation(['lab-settings']);
    const [addListDialogOpen, setAddListDialogOpen] = useState(false);
    const [editListDialogOpen, setEditListDialogOpen] = useState(false);
    const [materialPriceList, setMaterialPriceList] = useState<MaterialPriceList>({} as MaterialPriceList);
    const [materials, setMaterials] = useState<PriceListMaterial[]>([]);
    const [snackbarOpen, setSnackbarOpen] = useState({
        addedPriceList: false,
        updatedPriceList: false,
        deletedPriceList: false,
    });
    const [filters, setFilters] = useState({ freetext: '' });
    const [orderBy, setOrderBy] = useState<OrderBy>(DEFAULT_MATERIAL_PRICE_LIST_ORDER);
    const { showBoundary } = useDenthubErrorBoundary();

    const { loading: labMaterialsLoading } = useQuery(getLabMaterialsTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            filters: { active: true },
        },
        onCompleted({ labMaterials }) {
            setMaterials(labMaterials);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { materialPriceLists } = {}, loading: materialPriceListsLoading } = useQuery(
        getMaterialPriceListsTemplate,
        {
            onError(error) {
                showBoundary(error);
            },
        },
    );

    const [deleteMaterialPriceList] = useMutation(deleteMaterialPriceListTemplate, {
        variables: {
            priceListId: materialPriceList._id,
        },
        onCompleted: ({ deleteMaterialPriceList }) => {
            removeMaterialPriceList(deleteMaterialPriceList);
        },
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<MaterialPriceList[]>) {
            cache.modify({
                fields: {
                    materialPriceLists(existingPriceListRefs: Reference | (Reference | undefined)[], { readField }) {
                        if (existingPriceListRefs instanceof Array) {
                            return [...existingPriceListRefs].filter((priceListRef: Reference | undefined) => {
                                return materialPriceList._id !== readField('_id', priceListRef);
                            });
                        }
                        return [];
                    },
                },
            });
        },
    });

    const [addDefaultMaterialPriceList] = useMutation(addDefaultMaterialPriceListTemplate, {
        variables: {
            priceListId: materialPriceList._id,
        },
        onCompleted: ({ addDefaultMaterialPriceList }) => {
            defaultMaterialPriceList(addDefaultMaterialPriceList);
        },
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<MaterialPriceList>) {
            cache.evict({
                id: 'ROOT_QUERY',
                fieldName: 'materialPriceLists',
            });
            cache.gc();
        },
    });

    const [addMaterialToPriceList, { loading: addPriceLoading }] = useMutation(addMaterialToPriceListTemplate, {
        onCompleted: ({ addMaterialToPriceList }) => {
            const priceList = addMaterialToPriceList.find((priceList: MaterialPriceList) => {
                return priceList._id === materialPriceList._id;
            });
            setMaterialPriceList(priceList);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [editMaterialPriceListRow, { loading: editPriceLoading }] = useMutation(editMaterialPriceListRowTemplate, {
        onCompleted: ({ editMaterialPriceListRow }) => {
            const priceList = editMaterialPriceListRow.find((priceList: MaterialPriceList) => {
                return priceList._id === materialPriceList._id;
            });
            setMaterialPriceList(priceList);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteMaterialPriceListRow] = useMutation(deleteMaterialPriceListRowTemplate, {
        onCompleted: ({ deleteMaterialPriceListRow }) => {
            const priceList = deleteMaterialPriceListRow.find((priceList: MaterialPriceList) => {
                return priceList._id === materialPriceList._id;
            });
            setMaterialPriceList({ ...materialPriceList, materials: priceList.materials });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleCloseAddListDialog = () => {
        setAddListDialogOpen(false);
    };

    const handleCloseEditListDialog = () => {
        setEditListDialogOpen(false);
    };

    const handleSelectedPriceList = (selectedPriceList: string) => {
        const priceLists = [...materialPriceLists];
        const materialPriceList = priceLists.find(
            (list: MaterialPriceList) =>
                list.materialPriceListName.toLocaleLowerCase() === selectedPriceList.toLocaleLowerCase(),
        );
        if (materialPriceList) {
            setMaterialPriceList(materialPriceList);
        }
    };

    const handleFilterChange = (freetext: string) => {
        setFilters({ freetext: freetext });
    };
    const handleSortChange = (field: string) => {
        setOrderBy(toggleOrderBy(orderBy, field));
    };
    const filteredAndSortedPriceListMaterials = (materials: PriceListMaterial[]) => {
        const materialsArr = materials.filter(
            ({ description, materialCode }: PriceListMaterial) =>
                description?.toLocaleLowerCase().includes(filters.freetext.toLocaleLowerCase()) ||
                materialCode?.toLocaleLowerCase().includes(filters.freetext.toLocaleLowerCase()),
        );
        return sortPriceListMaterials(materialsArr, orderBy);
    };
    const getMaterials = (priceList: MaterialPriceList): PriceListMaterial[] => {
        const materialsArr = [...materials];
        const priceMaterials = priceList.materials?.map((material) => material);
        if (priceMaterials) {
            for (const priceMaterial of priceMaterials) {
                const index = materials.findIndex((material) => material._id === priceMaterial.material);
                materialsArr.splice(index, 1, {
                    ...materials[index],
                    _id: priceMaterial._id,
                    basePrice: priceMaterial.basePrice,
                    originalPrice: priceMaterial.originalPrice,
                });
            }
            return materialsArr;
        }
        return materialsArr;
    };

    const addMaterialPriceList = (priceList: MaterialPriceList) => {
        setMaterialPriceList(priceList);
        setSnackbarOpen({ ...snackbarOpen, addedPriceList: true });
    };
    const editMaterialPriceList = (priceList: MaterialPriceList) => {
        setMaterialPriceList(priceList);
        setSnackbarOpen({ ...snackbarOpen, updatedPriceList: true });
    };
    const removeMaterialPriceList = (priceListId: string) => {
        const priceLists = [...materialPriceLists];
        const index = priceLists.findIndex(
            (list: MaterialPriceList) => list._id?.toString() === priceListId.toString(),
        );
        if (index > -1) {
            priceLists.splice(index, 1);
        }
        setMaterialPriceList({} as MaterialPriceList);
        setSnackbarOpen({ ...snackbarOpen, deletedPriceList: true });
    };
    const defaultMaterialPriceList = (priceList: MaterialPriceList) => {
        setMaterialPriceList(priceList);
        setSnackbarOpen({ ...snackbarOpen, updatedPriceList: true });
    };

    const handleAddMaterial = async (material: PriceListMaterial) => {
        const priceWithTwoDecimals = parseFloat(material.basePrice || '0').toFixed(2);
        await addMaterialToPriceList({
            variables: {
                priceListId: materialPriceList._id,
                materialId: material._id,
                basePrice: parseFloat(priceWithTwoDecimals),
            },
        });
    };
    const handleEditMaterial = async (material: PriceListMaterial) => {
        const priceWithTwoDecimals = parseFloat(material.basePrice || '0').toFixed(2);
        await editMaterialPriceListRow({
            variables: {
                priceListId: materialPriceList._id,
                materialId: material._id,
                basePrice: parseFloat(priceWithTwoDecimals),
            },
        });
    };
    const handleRemoveMaterialPrice = async (material: PriceListMaterial) => {
        await deleteMaterialPriceListRow({
            variables: {
                priceListId: materialPriceList._id,
                materialId: material._id,
            },
        });
    };

    const getDefaultMaterialPriceList = () => {
        const defaultPriceList = materialPriceLists.find((list: MaterialPriceList) => list.isStandardPriceList);
        if (!defaultPriceList) return;
        setMaterialPriceList(defaultPriceList);
    };
    if (!materialPriceListsLoading && !materialPriceList._id) {
        getDefaultMaterialPriceList();
    }

    return (
        <Fragment>
            {materialPriceListsLoading && !labMaterialsLoading ? (
                <Loader />
            ) : (
                <Fragment>
                    <MaterialPriceListsActions
                        onNewMaterialListClick={() => setAddListDialogOpen(true)}
                        materialPriceLists={materialPriceLists || []}
                        materialPriceList={materialPriceList}
                        onSelectedPriceList={handleSelectedPriceList}
                    />
                    {materialPriceList.materialPriceListName && (
                        <Fragment>
                            <Box display="flex" alignItems="center">
                                <Box sx={{ cursor: 'pointer' }} onClick={() => setEditListDialogOpen(true)}>
                                    <Typography variant="h3">{materialPriceList.materialPriceListName}</Typography>
                                    {materialPriceList.materialListType ===
                                        MaterialListType.CURRENCY_CONVERTED_PRICE_LIST &&
                                    materialPriceList.conversionRate ? (
                                        <Typography variant="body2">
                                            {translate('lab-settings:CURRENCY_CONVERSION_RATE')}:{' '}
                                            {materialPriceList.conversionRate}
                                        </Typography>
                                    ) : undefined}
                                </Box>
                                <Tooltip
                                    title={translate('lab-settings:REMOVE_PRICE_LIST').toString()}
                                    placement="right"
                                >
                                    <IconButton
                                        sx={{
                                            visibility: materialPriceList.isStandardPriceList ? 'hidden' : 'visible',
                                            marginLeft: '4px',
                                        }}
                                        size="medium"
                                        onClick={() => deleteMaterialPriceList()}
                                    >
                                        <DeleteIcon fontSize="medium" />
                                    </IconButton>
                                </Tooltip>
                                {!materialPriceList.isStandardPriceList &&
                                    materialPriceList.materialListType !==
                                        MaterialListType.CURRENCY_CONVERTED_PRICE_LIST && (
                                        <Button sx={{ marginLeft: 1 }} onClick={() => addDefaultMaterialPriceList()}>
                                            {translate('lab-settings:USE_AS_STANDARD_LIST')}
                                        </Button>
                                    )}
                            </Box>
                            {materialPriceList.materials && materials.length >= 1 ? (
                                <Fragment>
                                    {/* TODO: Decide whether or not to use this compmonent instead of a regular text field for filter  */}
                                    <Box mb={1}>
                                        <MaterialPriceListsFilters onFilterChange={handleFilterChange} />
                                    </Box>
                                    <Box mb={2}>
                                        <Paper variant="outlined">
                                            <MaterialPriceListsItem
                                                materials={filteredAndSortedPriceListMaterials(
                                                    getMaterials(materialPriceList),
                                                )}
                                                onSaveMaterialPrice={handleAddMaterial}
                                                onEditMaterialPrice={handleEditMaterial}
                                                onRemoveMaterialPrice={handleRemoveMaterialPrice}
                                                conversionCurrency={materialPriceList.conversionCurrency}
                                                currency={materialPriceList.currency}
                                                isCurrencyConverted={
                                                    materialPriceList.materialListType ===
                                                    MaterialListType.CURRENCY_CONVERTED_PRICE_LIST
                                                }
                                                addPriceLoading={addPriceLoading}
                                                editPriceLoading={editPriceLoading}
                                                orderBy={orderBy}
                                                onSortChange={handleSortChange}
                                            />
                                        </Paper>
                                    </Box>
                                </Fragment>
                            ) : (
                                <Box padding={2}>
                                    <Alert severity="info">{translate('lab-settings:NO_ACTIVE_MATERIALS')}</Alert>
                                </Box>
                            )}
                        </Fragment>
                    )}
                    <AddMaterialPriceListDialog
                        dialogOpen={addListDialogOpen}
                        onCloseDialog={handleCloseAddListDialog}
                        onAddMaterialPriceList={addMaterialPriceList}
                        materialPriceLists={materialPriceLists}
                    />
                    <EditMaterialPriceListDialog
                        materialPriceList={materialPriceList}
                        dialogOpen={editListDialogOpen}
                        onCloseDialog={handleCloseEditListDialog}
                        onEditMaterialPriceList={editMaterialPriceList}
                        materialPriceLists={materialPriceLists}
                    />
                    {snackbarOpen.addedPriceList ? (
                        <AlertSnackbar type="success" message={translate('lab-settings:PRICE_LIST_ADDED')} />
                    ) : undefined}
                    {snackbarOpen.updatedPriceList ? (
                        <AlertSnackbar type="success" message={translate('lab-settings:PRICE_LIST_UPDATED')} />
                    ) : undefined}
                    {snackbarOpen.deletedPriceList ? (
                        <AlertSnackbar type="success" message={translate('lab-settings:PRICE_LIST_DELETED')} />
                    ) : undefined}
                </Fragment>
            )}
        </Fragment>
    );
};

export default MaterialPriceLists;
